import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
    errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary Component
 * ========================
 * Industry standard error handling for React apps.
 * Used by: Facebook, Airbnb, Netflix
 * 
 * Features:
 * - Catches React component errors
 * - Prevents whole app crash
 * - Provides fallback UI
 * - Logs errors for monitoring
 * - Allows graceful recovery
 * 
 * Usage:
 * <ErrorBoundary>
 *   <YourComponent />
 * </ErrorBoundary>
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null
        };
    }

    static getDerivedStateFromError(_error: Error): Partial<State> {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Log error to monitoring service
        console.error('Error Boundary caught error:', error, errorInfo);
        
        // Update state with error details
        this.setState({
            error,
            errorInfo
        });

        // Send to error tracking service (Sentry, etc.)
        if (window.location.hostname !== 'localhost') {
            this.logErrorToService(error, errorInfo);
        }
    }

    logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
        // Send error report to backend for monitoring
        try {
            const errorPayload = {
                message: error.message,
                stack: error.stack?.substring(0, 2000),
                componentStack: errorInfo.componentStack?.substring(0, 2000),
                url: window.location.href,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
            };

            // Use navigator.sendBeacon for reliability (works even during page unload)
            const apiBase = import.meta.env.VITE_API_URL || '';
            const blob = new Blob([JSON.stringify(errorPayload)], { type: 'application/json' });
            navigator.sendBeacon(`${apiBase}/api/v1/error-report`, blob);
        } catch {
            // Last resort: log to console if reporting itself fails
            console.error('Failed to report error to monitoring service');
        }
    };

    handleReset = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null
        });
    };

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI (industry standard design)
            return (
                <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#09090b] flex items-center justify-center p-6">
                    <div className="max-w-2xl w-full">
                        {/* Error Card */}
                        <div className="bg-white dark:bg-zinc-900 rounded-md p-8 md:p-12 border border-slate-200 dark:border-white/5 shadow-2xl">
                            
                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                                    <AlertTriangle className="w-10 h-10 text-red-600 dark:text-red-400" />
                                </div>
                            </div>

                            {/* Title */}
                            <h1 className="text-2xl md:text-3xl font-black text-center text-slate-900 dark:text-white mb-3">
                                Something Went Wrong
                            </h1>

                            {/* Message */}
                            <p className="text-center text-gray-600 dark:text-gray-400 mb-8 leading-relaxed">
                                We apologize for the inconvenience. Our team has been automatically notified 
                                and is working to fix the issue.
                            </p>

                            {/* Error Details (Development Only) */}
                            {import.meta.env.DEV && this.state.error && (
                                <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/20 rounded-md border border-red-200 dark:border-red-800">
                                    <h3 className="font-bold text-sm text-red-900 dark:text-red-100 mb-2">
                                        Error Details (Development Only)
                                    </h3>
                                    <p className="text-xs font-mono text-red-800 dark:text-red-200 mb-2">
                                        {this.state.error.message}
                                    </p>
                                    {this.state.errorInfo && (
                                        <details className="text-xs font-mono text-red-700 dark:text-red-300">
                                            <summary className="cursor-pointer hover:underline">
                                                Component Stack
                                            </summary>
                                            <pre className="mt-2 whitespace-pre-wrap">
                                                {this.state.errorInfo.componentStack}
                                            </pre>
                                        </details>
                                    )}
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    onClick={this.handleReset}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-[#FF4D00] text-white rounded-md font-bold hover:bg-[#e64500] transition-all hover:scale-105 active:scale-95"
                                >
                                    <RefreshCw size={18} />
                                    Try Again
                                </button>
                                
                                <button
                                    onClick={this.handleGoHome}
                                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-slate-900 dark:bg-white text-white dark:text-black rounded-md font-bold hover:bg-slate-800 dark:hover:bg-gray-100 transition-all hover:scale-105 active:scale-95"
                                >
                                    <Home size={18} />
                                    Go Home
                                </button>
                            </div>

                            {/* Alternative: Reload */}
                            <button
                                onClick={this.handleReload}
                                className="mt-4 w-full text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 font-medium transition-colors"
                            >
                                Or reload the page
                            </button>
                        </div>

                        {/* Support Link */}
                        <div className="text-center mt-6">
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Need help?{' '}
                                <a 
                                    href="/contact" 
                                    className="text-[#FF4D00] hover:underline font-medium"
                                >
                                    Contact Support
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;


/**
 * Hook-based error boundary wrapper (for functional components)
 */
export const withErrorBoundary = (Component: React.ComponentType<any>, fallback?: ReactNode) => {
    return (props: any) => (
        <ErrorBoundary fallback={fallback}>
            <Component {...props} />
        </ErrorBoundary>
    );
};
